import { useCallback, useState } from 'react';

import { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AuthAction from 'redux/actions/authActions/Actions';

import { useAuthContext } from 'hooks/useAuthContext';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { User } from 'models/User';

import { AuthService } from 'services/AuthService';
import { CompanyService } from 'services/CompanyService';

import { CompanyFormPayload } from '../types';

interface UseCompanyModalsParams {
  message: MessageInstance;
  fetchDataFromServer: (withLoading?: boolean) => Promise<void>;
  setTableLoading: (loading: boolean) => void;
}

export interface UseCompanyModalsReturn {
  /* State */
  isDetailModalOpen: boolean;
  isNewCompanyModalOpen: boolean;
  isMenuDetailModalOpen: boolean;
  isChangePasswordModalOpen: boolean;
  selectedRow: Company | undefined;
  selectedRowForPassword: Company | undefined;
  /* Open handlers */
  openNewCompanyModal: () => void;
  openDetailModal: (row: Company) => void;
  openMenuDetailModal: (row: Company) => void;
  openChangePasswordModal: (row: Company) => void;
  /* Close handlers */
  closeNewCompanyModal: () => void;
  closeDetailModal: () => void;
  closeMenuDetailModal: () => void;
  closeChangePasswordModal: () => void;
  /* Async actions */
  createNewCompany: (
    payload: CompanyFormPayload & { password: string }
  ) => Promise<void>;
  editCompany: (id: number, payload: CompanyFormPayload) => Promise<void>;
  handleChangePassword: (
    companyId: number,
    newPassword: string
  ) => Promise<void>;
}

export function useCompanyModals({
  message,
  fetchDataFromServer,
  setTableLoading,
}: UseCompanyModalsParams): UseCompanyModalsReturn {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuthContext();

  /**
   * La sesión guarda una copia de la compañía (sus integraciones incluidas), y
   * otras vistas deciden con ella qué mostrar. Si se edita la compañía propia
   * hay que releerla, o los cambios recién se ven al recargar la página.
   */
  const refreshSessionIfOwnCompany = useCallback(
    async (companyId: number) => {
      if (companyId !== user?.company?.id) return;

      const response = await AuthService.verifySession();

      if (response.success) {
        dispatch(AuthAction.signIn(response.data as User));
      }
    },
    [dispatch, user?.company?.id]
  );

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewCompanyModalOpen, setIsNewCompanyModalOpen] = useState(false);
  const [isMenuDetailModalOpen, setIsMenuDetailModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState<Company | undefined>(
    undefined
  );
  const [selectedRowForPassword, setSelectedRowForPassword] = useState<
    Company | undefined
  >(undefined);

  /* --- Open handlers --- */

  const openNewCompanyModal = useCallback(() => {
    setIsNewCompanyModalOpen(true);
  }, []);

  const openDetailModal = useCallback((row: Company) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  }, []);

  const openMenuDetailModal = useCallback((row: Company) => {
    setSelectedRow(row);
    setIsMenuDetailModalOpen(true);
  }, []);

  const openChangePasswordModal = useCallback((row: Company) => {
    setSelectedRowForPassword(row);
    setIsChangePasswordModalOpen(true);
  }, []);

  /* --- Close handlers --- */

  const closeNewCompanyModal = useCallback(() => {
    setIsNewCompanyModalOpen(false);
  }, []);

  const closeDetailModal = useCallback(() => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  }, []);

  const closeMenuDetailModal = useCallback(() => {
    setSelectedRow(undefined);
    setIsMenuDetailModalOpen(false);
  }, []);

  const closeChangePasswordModal = useCallback(() => {
    setSelectedRowForPassword(undefined);
    setIsChangePasswordModalOpen(false);
  }, []);

  /* --- Async actions --- */

  const createNewCompany = async (
    payload: CompanyFormPayload & { password: string }
  ) => {
    setTableLoading(true);

    const { password, countryCode, ...rest } = payload;

    const response = await CompanyService.create({
      ...rest,
      countryCode: countryCode as string,
      reportAccessSecret: password,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      message.success(
        t('scenes.settings.companies.scene.messages.create_company_success', {
          name: payload.name,
          id: (response.data as { createdId: number }).createdId,
        })
      );
      closeNewCompanyModal();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.create_company_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const editCompany = async (id: number, payload: CompanyFormPayload) => {
    setTableLoading(true);

    const response = await CompanyService.update(id, {
      name: payload.name,
      isPrivate: payload.isPrivate,
      kitIntegrationEnabled: payload.kitIntegrationEnabled,
      kitCompanyIds: payload.kitCompanyIds,
      tiendaPerfectaIntegrationEnabled:
        payload.tiendaPerfectaIntegrationEnabled,
      tiendaPerfectaCompanyCode: payload.tiendaPerfectaCompanyCode,
    });

    if (response.success) {
      await Promise.all([
        fetchDataFromServer(true),
        refreshSessionIfOwnCompany(id),
      ]);
      message.success(
        t('scenes.settings.companies.scene.messages.update_company_success', {
          name: payload.name,
          id,
        })
      );
      closeDetailModal();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.update_company_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const handleChangePassword = async (
    companyId: number,
    newPassword: string
  ) => {
    setTableLoading(true);

    const response = await CompanyService.updateReporteAccessSecret(companyId, {
      reportAccessSecret: newPassword,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      message.success(
        t('scenes.settings.companies.scene.messages.change_password_success'),
        3.5
      );
      closeChangePasswordModal();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.change_password_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  return {
    isDetailModalOpen,
    isNewCompanyModalOpen,
    isMenuDetailModalOpen,
    isChangePasswordModalOpen,
    selectedRow,
    selectedRowForPassword,
    openNewCompanyModal,
    openDetailModal,
    openMenuDetailModal,
    openChangePasswordModal,
    closeNewCompanyModal,
    closeDetailModal,
    closeMenuDetailModal,
    closeChangePasswordModal,
    createNewCompany,
    editCompany,
    handleChangePassword,
  };
}
